import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/lib/auth.store';
import { referralApi } from '@/lib/stage4.api';

export function InvitePage() {
  const user = useAuthStore((s) => s.user);
  const [code, setCode] = useState<string | null>(null);
  const [stats, setStats] = useState<{
    total: number;
    rewarded: number;
    qualified: number;
    pending: number;
  } | null>(null);
  const [list, setList] = useState<Awaited<ReturnType<typeof referralApi.list>>>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    void referralApi.me().then((r) => {
      setCode(r.inviteCode);
      setStats(r.stats);
    });
    void referralApi
      .list()
      .then(setList)
      .catch(() => setList([]));
  }, [user]);

  const link = code ? `${window.location.origin}/auth/register?invite=${code}` : '';

  const copy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!user) {
    return (
      <main className="flex flex-1 items-center justify-center px-4 sm:px-6">
        <Card className="max-w-md p-6 text-center sm:p-10">
          <p className="font-serif text-2xl text-fg mb-3">邀请你的朋友</p>
          <p className="text-sm text-fg-2 mb-6">登录后获取专属邀请码</p>
          <Link to="/auth/login">
            <Button size="lg">登录</Button>
          </Link>
        </Card>
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 py-8 sm:px-6 sm:py-12">
      <div className="max-w-3xl mx-auto space-y-6">
        <h1 className="font-serif text-3xl text-fg">邀请有礼</h1>
        <p className="text-sm text-fg-muted">
          每位被邀请人完成首单后，你将获得 ¥10 钱包奖励，对方也将获得新人首单券
        </p>

        <Card variant="gold" className="p-5 text-center sm:p-8">
          <p className="text-xs text-accent tracking-widest mb-2">你的专属邀请码</p>
          <p className="mb-5 break-all font-serif text-4xl tracking-widest text-fg sm:mb-6 sm:text-5xl">
            {code ?? '...'}
          </p>
          <div className="flex flex-col items-stretch justify-center gap-2 text-xs text-fg-muted sm:flex-row sm:items-center">
            <code className="px-3 py-1.5 bg-bg-1 rounded border border-border break-all">
              {link}
            </code>
            <Button size="sm" variant="outline" onClick={copy}>
              {copied ? '已复制' : '复制链接'}
            </Button>
          </div>
        </Card>

        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="累计邀请" value={stats.total} />
            <Stat label="已发奖" value={stats.rewarded} />
            <Stat label="待发奖" value={stats.qualified} />
            <Stat label="未达标" value={stats.pending} />
          </div>
        )}

        <Card className="p-5">
          <p className="text-sm text-fg mb-4">邀请记录</p>
          {list.length === 0 ? (
            <p className="text-xs text-fg-muted">暂无邀请记录，分享上面的链接邀请朋友吧</p>
          ) : (
            <div className="space-y-2">
              {list.map((r) => (
                <div
                  key={r.id}
                  className="flex flex-col gap-2 border-b border-border py-2 text-sm last:border-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <span className="text-fg">{r.invitee.name ?? r.invitee.email}</span>
                    <span className="text-xs text-fg-muted ml-2">
                      {new Date(r.invitee.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <span
                    className={
                      'text-xs px-2 py-1 rounded ' +
                      (r.status === 'rewarded'
                        ? 'bg-accent-soft text-accent'
                        : r.status === 'qualified'
                          ? 'text-fg-2'
                          : 'text-fg-muted')
                    }
                  >
                    {STATUS_LABEL[r.status]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-4 text-center">
      <p className="text-2xl text-accent font-serif">{value}</p>
      <p className="text-xs text-fg-muted mt-1">{label}</p>
    </Card>
  );
}

const STATUS_LABEL = {
  pending: '待付费',
  qualified: '待结算',
  rewarded: '已发奖',
  rejected: '风控拒绝',
} as const;
